import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import './App.css';

import Header from './components/Header';
import Chatbot from './components/Chatbot';
import StudentInfo from './components/StudentInfo';
import Uploader from './components/Uploader';
import RsvpHero from './components/RsvpHero';
import WelcomeAudio from './components/WelcomeAudio';

function RsvpRoute() {
  const { id } = useParams();
  return <RsvpView studentId={id} />;
}

function UploadRoute() {
  const { id } = useParams();
  return <Uploader initialStudentId={id} />;
}

function RsvpView({ studentId: initialId }) {
  const [studentId, setStudentId] = useState(initialId || null);
  const [studentData, setStudentData] = useState(null);
  const [rsvpData, setRsvpData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const fetchStudentData = async () => {
      if (!studentId) {
        setStudentData(null);
        setRsvpData(null);
        setErrorMessage(null);
        return;
      }

      try {
        setLoading(true);
        const studentRef = doc(db, "students", studentId);
        const studentSnap = await getDoc(studentRef);

        if (studentSnap.exists()) {
          setStudentData(studentSnap.data());
          setErrorMessage(null);

          const rsvpRef = doc(db, "rsvps", studentId);
          const rsvpSnap = await getDoc(rsvpRef);
          setRsvpData(rsvpSnap.exists() ? rsvpSnap.data() : null);
        } else {
          // Staff were accidentally emailed cte-rsvp.joshuabailey.us/{username}
          // URLs that should have pointed at staff-rsvp. If the id matches a
          // staff record (by username or email), bounce them over.
          const staffSnap = await getDoc(doc(db, "staff", studentId));
          let staffMatch = staffSnap.exists();
          if (!staffMatch) {
            const emailSnap = await getDoc(doc(db, "staffEmailLookup", studentId.toLowerCase()));
            staffMatch = emailSnap.exists();
          }
          if (staffMatch) {
            window.location.replace(`https://staff-rsvp.joshuabailey.us/${encodeURIComponent(studentId)}`);
            return;
          }

          setStudentData(null);
          setRsvpData(null);
          setErrorMessage("We couldn't find a CTE completer with that student ID. Double-check the number on your invite and try again.");
        }
      } catch (error) {
        console.error("Error fetching student data:", error);
        setErrorMessage("Trouble looking up your information. Please try again in a moment.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId]);

  const handleStudentIdSubmit = (id) => {
    setStudentId(id);
    if (id) {
      window.history.pushState(null, '', `/${id}`);
    } else {
      window.history.pushState(null, '', '/');
    }
  };

  const handleRsvpSubmit = async (rsvp) => {
    try {
      const rsvpRef = doc(db, "rsvps", studentId);

      // Always setDoc with merge so this works whether the canonical doc
      // exists yet or not. The earlier updateDoc branch failed silently for
      // first-time submissions because tempRsvpData.createdAt is set during
      // wizard init, sending us down the updateDoc path against a doc that
      // didn't exist yet.
      await setDoc(rsvpRef, {
        ...rsvp,
        createdAt: rsvp.createdAt || new Date(),
        updatedAt: new Date(),
        studentId
      }, { merge: true });

      setRsvpData({ ...rsvp, updatedAt: new Date() });
      return true;
    } catch (error) {
      console.error("Error saving RSVP:", error);
      return false;
    }
  };

  return (
    <>
      <WelcomeAudio />
      <RsvpHero />
      <Chatbot
        onStudentIdSubmit={handleStudentIdSubmit}
        onRsvpSubmit={handleRsvpSubmit}
        studentData={studentData}
        rsvpData={rsvpData}
        loading={loading}
        errorMessage={errorMessage}
        hasUrlStudentId={!!initialId}
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="app">
        <Header />
        <div className="container">
          <Routes>
            <Route path="/upload" element={<UploadRoute />} />
            <Route path="/upload/:id" element={<UploadRoute />} />
            <Route path="/student-info" element={<StudentInfoFallback />} />
            <Route path="/" element={<RsvpView studentId={null} />} />
            <Route path="/:id" element={<RsvpRoute />} />
          </Routes>
        </div>
        <footer className="site-footer">
          <p>Veterans Memorial High School · CTE Senior Awards Ceremony · May 4, 2026</p>
          <p>
            Questions?{' '}
            <a href="mailto:rneal@judsonisd.org">rneal@judsonisd.org</a>
          </p>
        </footer>
      </div>
    </Router>
  );
}

function StudentInfoFallback() {
  return <Navigate to="/" replace />;
}

export default App;
